/**
 * Video compression utility for client-side optimization
 * Note: Browser-based video compression is limited compared to server-side tools
 */

class VideoCompressor {
  /**
   * Basic video optimization using HTML5 video element
   * This mainly re-encodes the video with different quality settings
   * @param {File} file - The video file to compress
   * @param {Object} options - Compression options
   * @returns {Promise<File>} - Optimized video file
   */
  static async compressVideo(file, options = {}) {
    const {
      maxWidth = 1280,
      maxHeight = 720,
      quality = 0.7,
      frameRate = 30
    } = options;

    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      video.onloadedmetadata = () => {
        try {
          // Calculate new dimensions while maintaining aspect ratio
          let { videoWidth: width, videoHeight: height } = video;
          
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = Math.floor(width * ratio);
            height = Math.floor(height * ratio);
          }

          canvas.width = width;
          canvas.height = height;

          // Create MediaRecorder for re-encoding
          const stream = canvas.captureStream(frameRate);
          const mediaRecorder = new MediaRecorder(stream, {
            mimeType: 'video/webm;codecs=vp9',
            videoBitsPerSecond: Math.floor(width * height * frameRate * quality)
          });

          const chunks = [];
          mediaRecorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
              chunks.push(event.data);
            }
          };

          mediaRecorder.onstop = () => {
            const compressedBlob = new Blob(chunks, { type: 'video/webm' });
            const compressedFile = new File([compressedBlob], 
              file.name.replace(/\.[^/.]+$/, '.webm'), {
              type: 'video/webm',
              lastModified: Date.now()
            });
            resolve(compressedFile);
          };

          // Start recording and play video
          video.play();
          mediaRecorder.start();

          // Draw frames to canvas during playback
          const drawFrame = () => {
            if (!video.paused && !video.ended) {
              ctx.drawImage(video, 0, 0, width, height);
              requestAnimationFrame(drawFrame);
            } else {
              mediaRecorder.stop();
            }
          };

          video.addEventListener('play', drawFrame);

        } catch (error) {
          reject(new Error(`Video compression failed: ${error.message}`));
        }
      };

      video.onerror = () => {
        URL.revokeObjectURL(video.src);
        reject(new Error('Failed to load video'));
      };
      const videoUrl = URL.createObjectURL(file);
      video.src = videoUrl;
    });
  }

  /**
   * Check if video compression would be beneficial
   * @param {File} file - The video file
   * @param {number} threshold - File size threshold in bytes (default 15MB)
   * @returns {boolean}
   */
  static shouldCompress(file, threshold = 15 * 1024 * 1024) {
    return file.size > threshold;
  }

  /**
   * Get basic video file information
   * @param {File} file - The video file
   * @returns {Promise<Object>} - Video information
   */
  static async getVideoInfo(file) {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');

      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        const info = {
          duration: video.duration,
          width: video.videoWidth,
          height: video.videoHeight,
          size: file.size,
          sizeMB: (file.size / (1024 * 1024)).toFixed(2),
          type: file.type
        };
        resolve(info);
      };

      video.onerror = () => {
        URL.revokeObjectURL(video.src);
        reject(new Error('Failed to load video file'));
      };

      video.src = URL.createObjectURL(file);
    });
  }

  /**
   * Get compression statistics
   * @param {File} originalFile 
   * @param {File} compressedFile 
   * @returns {Object}
   */
  static getCompressionStats(originalFile, compressedFile) {
    const originalSize = originalFile.size;
    const compressedSize = compressedFile.size;
    const savedBytes = originalSize - compressedSize;
    const compressionRatio = ((savedBytes / originalSize) * 100).toFixed(1);

    return {
      originalSize,
      compressedSize,
      savedBytes,
      compressionRatio: `${compressionRatio}%`,
      originalSizeMB: (originalSize / (1024 * 1024)).toFixed(2),
      compressedSizeMB: (compressedSize / (1024 * 1024)).toFixed(2)
    };
  }

  /**
   * Simple video validation and size check
   * @param {File} file - The video file
   * @param {number} maxSizeMB - Maximum size in MB
   * @returns {Object} - Validation result
   */
  static validateVideo(file, maxSizeMB = 25) {
    const sizeMB = file.size / (1024 * 1024);
    const isValidType = file.type.startsWith('video/');
    const isValidSize = sizeMB <= maxSizeMB;

    return {
      isValid: isValidType && isValidSize,
      errors: [
        !isValidType && 'Please select a valid video file',
        !isValidSize && `Video file is too large (${sizeMB.toFixed(2)}MB). Maximum size is ${maxSizeMB}MB.`
      ].filter(Boolean),
      sizeMB: sizeMB.toFixed(2)
    };
  }
}

export default VideoCompressor; 